import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface Category {
  id: string;
  name_ar: string;
  name_en: string;
  icon: string;
  color: string;
  path: string;
  category_slug?: string;
  is_visible: boolean;
  display_order: number;
  is_system: boolean;
  events_limit: number;
  created_at: string;
  updated_at: string;
}

export const useCategories = () => {
  const queryClient = useQueryClient();

  const { data: categories = [], isLoading, error } = useQuery({
    queryKey: ["categories"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("categories" as any)
        .select("*")
        .order("display_order", { ascending: true });
      
      if (error) throw error;
      return (data || []) as unknown as Category[];
    },
  });

  const { data: visibleCategories = [] } = useQuery({
    queryKey: ["categories", "visible"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("categories" as any)
        .select("*")
        .eq("is_visible", true)
        .order("display_order", { ascending: true });
      
      if (error) throw error;
      return (data || []) as unknown as Category[];
    },
  });

  const addCategory = useMutation({
    mutationFn: async (category: Omit<Category, "id" | "created_at" | "updated_at">) => {
      const { data, error } = await supabase
        .from("categories" as any)
        .insert([category])
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["categories"] });
      toast.success("تم إضافة القسم بنجاح");
    },
    onError: (error) => {
      toast.error("فشل في إضافة القسم: " + error.message);
    },
  });

  const updateCategory = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<Category> & { id: string }) => {
      const { data, error } = await supabase
        .from("categories" as any)
        .update(updates)
        .eq("id", id)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["categories"] });
      toast.success("تم تحديث القسم بنجاح");
    },
    onError: (error) => {
      toast.error("فشل في تحديث القسم: " + error.message);
    },
  });

  const deleteCategory = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("categories" as any)
        .delete()
        .eq("id", id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["categories"] });
      toast.success("تم حذف القسم بنجاح");
    },
    onError: (error) => {
      toast.error("فشل في حذف القسم: " + error.message);
    },
  });

  const toggleVisibility = useMutation({
    mutationFn: async ({ id, is_visible }: { id: string; is_visible: boolean }) => {
      const { data, error } = await supabase
        .from("categories" as any)
        .update({ is_visible })
        .eq("id", id)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["categories"] });
    },
    onError: (error) => {
      toast.error("فشل في تحديث الحالة: " + error.message);
    },
  });

  const updateOrder = useMutation({
    mutationFn: async (orderedCategories: { id: string; display_order: number }[]) => {
      // Update each category's display_order
      for (const cat of orderedCategories) {
        const { error } = await supabase
          .from("categories" as any)
          .update({ display_order: cat.display_order })
          .eq("id", cat.id);
        
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["categories"] });
      toast.success("تم تحديث ترتيب الأقسام");
    },
    onError: (error) => {
      toast.error("فشل في تحديث الترتيب: " + error.message);
    },
  });

  return {
    categories,
    visibleCategories,
    isLoading,
    error,
    addCategory,
    updateCategory,
    deleteCategory,
    toggleVisibility,
    updateOrder,
  };
};
